// ============================================================================
// PHASE CONFIGURATOR COMPONENT
// Drag-and-drop configuration of artifacts and entities per journey phase
// ============================================================================

import { useState, useMemo } from 'react'
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { usePhaseConfig } from '@/hooks/usePhaseConfig'
import {
  ChevronRight,
  ChevronDown,
  GripVertical,
  X,
  Users,
  FileText,
  Save,
  Loader2,
  AlertCircle,
  Folder,
  FolderOpen,
  RefreshCw,
} from 'lucide-react'
import type { ConfigurableItem, PhaseArtifact, PhaseEntity, JourneyPhase, JourneyDomain } from '@ispot/shared'

// ============================================================================
// CONSTANTS
// ============================================================================

const DOMAIN_COLORS: Record<JourneyDomain, string> = {
  market: '#00b894',
  business: '#0984e3',
  solution: '#f9c148',
}

const DOMAIN_LABELS: Record<JourneyDomain, string> = {
  market: 'Market',
  business: 'Business',
  solution: 'Solution',
}

// ============================================================================
// COMPACT TREE ITEM (Draggable)
// ============================================================================

interface TreeItemProps {
  item: ConfigurableItem
  disabled?: boolean
}

function TreeItem({ item, disabled }: TreeItemProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `pool-${item.type}-${item.id}`,
    data: { item, source: 'pool' },
    disabled,
  })

  const domainColor = DOMAIN_COLORS[item.domain]

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 6px',
        marginLeft: 4,
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'grab',
        opacity: isDragging ? 0.5 : disabled ? 0.5 : 1,
        background: isDragging ? 'var(--color-warm-100)' : 'transparent',
        transition: 'background 100ms ease',
      }}
      onMouseEnter={(e) => {
        if (!isDragging && !disabled) {
          e.currentTarget.style.background = 'var(--color-warm-100)'
        }
      }}
      onMouseLeave={(e) => {
        if (!isDragging) {
          e.currentTarget.style.background = 'transparent'
        }
      }}
    >
      <GripVertical style={{ width: 12, height: 12, color: 'var(--color-warm-300)', flexShrink: 0 }} />
      <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: domainColor, flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {item.name}
      </span>
    </div>
  )
}

// Drag overlay version (shown while dragging)
function DraggablePoolItem({ item, disabled }: TreeItemProps) {
  const domainColor = DOMAIN_COLORS[item.domain]
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        background: 'white',
        border: '1px solid var(--color-warm-200)',
        borderRadius: 6,
        boxShadow: '0 4px 12px rgba(29, 26, 21, 0.15)',
        fontSize: 13,
      }}
    >
      <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: domainColor }} />
      <span>{item.name}</span>
    </div>
  )
}

// ============================================================================
// CONFIGURED ITEM (in drop zone)
// ============================================================================

interface ConfiguredItemProps {
  item: PhaseArtifact | PhaseEntity
  type: 'artifact' | 'entity'
  domain: JourneyDomain
  onRemove: () => void
  onToggleRequired: (isRequired: boolean) => void
  onMinCountChange?: (minCount: number) => void
}

function ConfiguredItem({ item, type, domain, onRemove, onToggleRequired, onMinCountChange }: ConfiguredItemProps) {
  const domainColor = DOMAIN_COLORS[domain]
  const isEntity = type === 'entity'
  const entityItem = item as PhaseEntity

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        background: 'white',
        border: '1px solid var(--color-warm-200)',
        borderRadius: '8px',
      }}
    >
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: 4,
          backgroundColor: `${domainColor}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: domainColor,
        }}
      >
        {type === 'artifact' ? <FileText style={{ width: 12, height: 12 }} /> : <Users style={{ width: 12, height: 12 }} />}
      </div>
      <span style={{ flex: 1, fontSize: 13, fontWeight: item.isRequired ? 600 : 500 }}>
        {'name' in item ? item.name : entityItem.label}
        {isEntity && entityItem.minCount > 0 && (
          <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}> (min: {entityItem.minCount})</span>
        )}
      </span>
      <Badge
        style={{
          fontSize: 10,
          padding: '2px 6px',
          backgroundColor: `${domainColor}15`,
          color: domainColor,
          border: 'none',
        }}
      >
        {DOMAIN_LABELS[domain]}
      </Badge>
      {isEntity && onMinCountChange && (
        <Input
          type="number"
          min={0}
          value={entityItem.minCount}
          onChange={(e) => onMinCountChange(parseInt(e.target.value) || 0)}
          style={{ width: 60, height: 28, fontSize: 12 }}
        />
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
        <Switch checked={item.isRequired} onCheckedChange={onToggleRequired} />
        Required
      </label>
      <button
        onClick={onRemove}
        style={{
          width: 24,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          background: 'transparent',
          color: 'var(--color-text-muted)',
          cursor: 'pointer',
          borderRadius: 4,
        }}
      >
        <X style={{ width: 14, height: 14 }} />
      </button>
    </div>
  )
}

// ============================================================================
// DROP ZONE
// ============================================================================

interface DropZoneProps {
  phaseId: string
  type: 'artifact' | 'entity'
  items: Array<PhaseArtifact | PhaseEntity>
  onRemove: (index: number) => void
  onToggleRequired: (index: number, isRequired: boolean) => void
  onMinCountChange?: (index: number, minCount: number) => void
  getDomain: (item: PhaseArtifact | PhaseEntity) => JourneyDomain
}

function DropZone({ phaseId, type, items, onRemove, onToggleRequired, onMinCountChange, getDomain }: DropZoneProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: `${phaseId}-${type}s`,
    data: { phaseId, type },
  })

  return (
    <div
      ref={setNodeRef}
      style={{
        background: 'var(--color-warm-50)',
        border: `2px dashed ${isOver ? 'var(--color-business)' : 'var(--color-warm-300)'}`,
        borderRadius: 10,
        padding: 16,
        minHeight: 140,
        flex: 1,
        backgroundColor: isOver ? 'rgba(9, 132, 227, 0.05)' : undefined,
        transition: 'all 200ms ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--color-text-secondary)' }}>
          {type === 'artifact' ? 'Artifacts' : 'Entities'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{items.length} items</span>
      </div>
      {items.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', color: 'var(--color-text-muted)', textAlign: 'center' }}>
          {type === 'artifact' ? <FileText style={{ width: 32, height: 32, marginBottom: 8, opacity: 0.5 }} /> : <Users style={{ width: 32, height: 32, marginBottom: 8, opacity: 0.5 }} />}
          <span style={{ fontSize: 13 }}>Drop {type}s here</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item, index) => (
            <ConfiguredItem
              key={type === 'artifact' ? (item as PhaseArtifact).id : (item as PhaseEntity).classUri}
              item={item}
              type={type}
              domain={getDomain(item)}
              onRemove={() => onRemove(index)}
              onToggleRequired={(isRequired) => onToggleRequired(index, isRequired)}
              onMinCountChange={type === 'entity' ? (minCount) => onMinCountChange?.(index, minCount) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// PHASE CARD
// ============================================================================

interface PhaseCardProps {
  phase: JourneyPhase
  isExpanded: boolean
  onToggle: () => void
  artifacts: PhaseArtifact[]
  entities: PhaseEntity[]
  onArtifactsChange: (artifacts: PhaseArtifact[]) => void
  onEntitiesChange: (entities: PhaseEntity[]) => void
  getArtifactDomain: (artifact: PhaseArtifact) => JourneyDomain
  getEntityDomain: (entity: PhaseEntity) => JourneyDomain
}

function PhaseCard({ phase, isExpanded, onToggle, artifacts, entities, onArtifactsChange, onEntitiesChange, getArtifactDomain, getEntityDomain }: PhaseCardProps) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: 12,
        border: `1px solid ${isExpanded ? 'var(--color-warm-300)' : 'var(--color-warm-200)'}`,
        boxShadow: '0 1px 2px rgba(29, 26, 21, 0.05)',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '16px 20px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: phase.color,
            color: 'white',
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {phase.order}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{phase.name}</span>
            {phase.type === 'iteration' && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                fontSize: 10,
                padding: '2px 6px',
                background: 'var(--color-warm-100)',
                borderRadius: 4,
                color: 'var(--color-text-muted)',
              }}>
                <RefreshCw style={{ width: 10, height: 10 }} />
                Iteration
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{phase.objective}</div>
        </div>
        <div style={{ display: 'flex', gap: 12, marginRight: 8 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{artifacts.length}</div>
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Artifacts</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{entities.length}</div>
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Entities</div>
          </div>
        </div>
        <ChevronDown
          style={{
            width: 20,
            height: 20,
            color: 'var(--color-text-muted)',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease',
          }}
        />
      </button>
      {isExpanded && (
        <div style={{ padding: '0 20px 20px', borderTop: '1px solid var(--color-warm-200)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 16, paddingTop: 16 }}>
            <DropZone
              phaseId={phase.id}
              type="artifact"
              items={artifacts}
              onRemove={(index) => onArtifactsChange(artifacts.filter((_, i) => i !== index))}
              onToggleRequired={(index, isRequired) => onArtifactsChange(artifacts.map((a, i) => i === index ? { ...a, isRequired } : a))}
              getDomain={getArtifactDomain}
            />
            <DropZone
              phaseId={phase.id}
              type="entity"
              items={entities}
              onRemove={(index) => onEntitiesChange(entities.filter((_, i) => i !== index))}
              onToggleRequired={(index, isRequired) => onEntitiesChange(entities.map((e, i) => i === index ? { ...e, isRequired } : e))}
              onMinCountChange={(index, minCount) => onEntitiesChange(entities.map((e, i) => i === index ? { ...e, minCount } : e))}
              getDomain={getEntityDomain}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PhaseConfigurator() {
  const { framework, phases, configurableItems, loading, saving, error, updatePhaseConfig, refresh } = usePhaseConfig()
  const [expandedPhaseId, setExpandedPhaseId] = useState<string | null>(null)
  const [activeItem, setActiveItem] = useState<ConfigurableItem | null>(null)
  const [unsavedChanges, setUnsavedChanges] = useState<Record<string, { artifacts: PhaseArtifact[]; entities: PhaseEntity[] }>>({})
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Collapsible sections state
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }

  // Group configurable items by domain
  const groupedArtifacts = useMemo(() => {
    if (!configurableItems) return {}
    return configurableItems.artifacts.reduce((acc, item) => {
      const domain = item.domain
      if (!acc[domain]) acc[domain] = []
      acc[domain].push(item)
      return acc
    }, {} as Record<JourneyDomain, ConfigurableItem[]>)
  }, [configurableItems])

  const groupedEntities = useMemo(() => {
    if (!configurableItems) return {}
    return configurableItems.entities.reduce((acc, item) => {
      const domain = item.domain
      if (!acc[domain]) acc[domain] = []
      acc[domain].push(item)
      return acc
    }, {} as Record<JourneyDomain, ConfigurableItem[]>)
  }, [configurableItems])

  // Get current config for a phase (unsaved or from framework)
  const getPhaseConfig = (phaseId: string) => {
    if (unsavedChanges[phaseId]) return unsavedChanges[phaseId]
    const phase = phases.find(p => p.id === phaseId)
    if (!phase) return { artifacts: [], entities: [] }
    return { artifacts: phase.artifacts, entities: phase.entities }
  }

  // Get domain for an artifact
  const getArtifactDomain = (artifact: PhaseArtifact): JourneyDomain => {
    const item = configurableItems?.artifacts.find(a => a.id === artifact.id)
    return item?.domain || 'market'
  }

  // Get domain for an entity
  const getEntityDomain = (entity: PhaseEntity): JourneyDomain => {
    const item = configurableItems?.entities.find(e => e.classUri === entity.classUri)
    return item?.domain || 'market'
  }

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { item } = event.active.data.current as { item: ConfigurableItem }
    setActiveItem(item)
  }

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    setActiveItem(null)
    const { active, over } = event
    if (!over) return

    const { item } = active.data.current as { item: ConfigurableItem }
    const { phaseId, type } = over.data.current as { phaseId: string; type: string }

    if (!phaseId || !type) return
    if (item.type !== type) return // Can't drop artifact in entity zone or vice versa

    const currentConfig = getPhaseConfig(phaseId)

    if (type === 'artifact') {
      // Check if already exists
      if (currentConfig.artifacts.some(a => a.id === item.id)) return

      const newArtifact: PhaseArtifact = {
        id: item.id,
        name: item.name,
        type: item.artifactType || 'doc',
        templateId: item.templateId,
        isRequired: false,
      }

      setUnsavedChanges(prev => ({
        ...prev,
        [phaseId]: {
          ...currentConfig,
          artifacts: [...currentConfig.artifacts, newArtifact],
        },
      }))
    } else {
      // Entity
      if (currentConfig.entities.some(e => e.classUri === item.classUri)) return

      const newEntity: PhaseEntity = {
        classUri: item.classUri || '',
        label: item.name,
        minCount: 1,
        isRequired: false,
      }

      setUnsavedChanges(prev => ({
        ...prev,
        [phaseId]: {
          ...currentConfig,
          entities: [...currentConfig.entities, newEntity],
        },
      }))
    }
  }

  // Handle artifacts/entities change for a phase
  const handleArtifactsChange = (phaseId: string, artifacts: PhaseArtifact[]) => {
    const currentConfig = getPhaseConfig(phaseId)
    setUnsavedChanges(prev => ({
      ...prev,
      [phaseId]: { ...currentConfig, artifacts },
    }))
  }

  const handleEntitiesChange = (phaseId: string, entities: PhaseEntity[]) => {
    const currentConfig = getPhaseConfig(phaseId)
    setUnsavedChanges(prev => ({
      ...prev,
      [phaseId]: { ...currentConfig, entities },
    }))
  }

  // Save all changes
  const handleSave = async () => {
    const phaseIds = Object.keys(unsavedChanges)
    for (const phaseId of phaseIds) {
      const config = unsavedChanges[phaseId]
      await updatePhaseConfig(phaseId, config)
    }
    setUnsavedChanges({})
    setSaveSuccess(true)
    setTimeout(() => setSaveSuccess(false), 2000)
  }

  // Reset changes
  const handleReset = () => {
    setUnsavedChanges({})
    refresh()
  }

  const hasUnsavedChanges = Object.keys(unsavedChanges).length > 0

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
        <Loader2 style={{ width: 24, height: 24, animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 8 }}>Loading configuration...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 24, color: '#c75050' }}>
        <AlertCircle style={{ width: 20, height: 20 }} />
        <span>{error}</span>
      </div>
    )
  }

  return (
    <DndContext collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20, minHeight: 'calc(100vh - 200px)' }}>
        {/* Left Panel: Compact Tree View */}
        <div
          style={{
            background: 'white',
            borderRadius: 10,
            border: '1px solid var(--color-warm-200)',
            boxShadow: '0 1px 2px rgba(29, 26, 21, 0.05)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-warm-200)', background: 'var(--color-warm-50)' }}>
            <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>Available Items</h2>
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>Drag to phases →</p>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
            {/* Artifacts by Domain */}
            {(['market', 'business', 'solution'] as JourneyDomain[]).map(domain => {
              const artifacts = groupedArtifacts[domain] || []
              const entities = groupedEntities[domain] || []
              const totalCount = artifacts.length + entities.length
              if (totalCount === 0) return null
              const domainKey = `domain-${domain}`
              const isExpanded = !collapsedSections.has(domainKey)
              const FolderIcon = isExpanded ? FolderOpen : Folder

              return (
                <div key={domain} style={{ marginBottom: 2 }}>
                  {/* Domain Header */}
                  <button
                    onClick={() => toggleSection(domainKey)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '5px 8px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: 4,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-warm-50)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                  >
                    <ChevronRight
                      style={{
                        width: 12,
                        height: 12,
                        color: 'var(--color-text-muted)',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 150ms ease',
                      }}
                    />
                    <FolderIcon style={{ width: 14, height: 14, color: DOMAIN_COLORS[domain] }} />
                    <span style={{ fontSize: 12, fontWeight: 500, color: DOMAIN_COLORS[domain] }}>{DOMAIN_LABELS[domain]} Domain</span>
                  </button>

                  {isExpanded && (
                    <div style={{ marginLeft: 20 }}>
                      {/* Artifacts subgroup */}
                      {artifacts.length > 0 && (
                        <div style={{ marginBottom: 2 }}>
                          <button
                            onClick={() => toggleSection(`${domainKey}-artifacts`)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                              padding: '3px 6px',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              borderRadius: 3,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-warm-50)' }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                          >
                            <ChevronRight
                              style={{
                                width: 10,
                                height: 10,
                                color: 'var(--color-text-muted)',
                                transform: !collapsedSections.has(`${domainKey}-artifacts`) ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 150ms ease',
                              }}
                            />
                            <FileText style={{ width: 12, height: 12, color: 'var(--color-text-muted)' }} />
                            <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>Artifacts</span>
                            <span style={{ fontSize: 10, color: 'var(--color-text-muted)', marginLeft: 'auto' }}>{artifacts.length}</span>
                          </button>
                          {!collapsedSections.has(`${domainKey}-artifacts`) && (
                            <div style={{ marginLeft: 16 }}>
                              {artifacts.map(item => <TreeItem key={item.id} item={item} />)}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Entities subgroup */}
                      {entities.length > 0 && (
                        <div>
                          <button
                            onClick={() => toggleSection(`${domainKey}-entities`)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                              padding: '3px 6px',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              borderRadius: 3,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-warm-50)' }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                          >
                            <ChevronRight
                              style={{
                                width: 10,
                                height: 10,
                                color: 'var(--color-text-muted)',
                                transform: !collapsedSections.has(`${domainKey}-entities`) ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 150ms ease',
                              }}
                            />
                            <Users style={{ width: 12, height: 12, color: 'var(--color-text-muted)' }} />
                            <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>Entities</span>
                            <span style={{ fontSize: 10, color: 'var(--color-text-muted)', marginLeft: 'auto' }}>{entities.length}</span>
                          </button>
                          {!collapsedSections.has(`${domainKey}-entities`) && (
                            <div style={{ marginLeft: 16 }}>
                              {entities.map(item => <TreeItem key={item.id} item={item} />)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Right Panel: Phase Configuration - All 7 phases including iterations */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {phases.map(phase => {
            const config = getPhaseConfig(phase.id)
            return (
              <PhaseCard
                key={phase.id}
                phase={phase}
                isExpanded={expandedPhaseId === phase.id}
                onToggle={() => setExpandedPhaseId(expandedPhaseId === phase.id ? null : phase.id)}
                artifacts={config.artifacts}
                entities={config.entities}
                onArtifactsChange={(artifacts) => handleArtifactsChange(phase.id, artifacts)}
                onEntitiesChange={(entities) => handleEntitiesChange(phase.id, entities)}
                getArtifactDomain={getArtifactDomain}
                getEntityDomain={getEntityDomain}
              />
            )
          })}

          {/* Action Bar */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 16, borderTop: '1px solid var(--color-warm-200)', marginTop: 16 }}>
            <Button variant="outline" onClick={handleReset} disabled={!hasUnsavedChanges}>
              Reset to Defaults
            </Button>
            <Button onClick={handleSave} disabled={!hasUnsavedChanges || saving}>
              {saving ? (
                <>
                  <Loader2 style={{ width: 16, height: 16, marginRight: 8, animation: 'spin 1s linear infinite' }} />
                  Saving...
                </>
              ) : saveSuccess ? (
                <>Saved!</>
              ) : (
                <>
                  <Save style={{ width: 16, height: 16, marginRight: 8 }} />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeItem && <DraggablePoolItem item={activeItem} />}
      </DragOverlay>
    </DndContext>
  )
}
